import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  House, Users, Plus, GitBranch, Bell, Settings, LogOut, Menu, X,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { ErasePendingBanner } from './ErasePendingBanner'
import { GenderPromptModal } from './GenderPromptModal'
import { resolveMediaUrl } from '../utils/media'
import { useAlertsCount } from '../api/hooks/useAlertsCount'

// Layout: full-width top bar above a fixed sidebar + scrolling main.
// The single hamburger on the left of the top bar drives both behaviours:
//   - on desktop (≥md), toggles the sidebar between expanded (~240px) and
//     a collapsed icon rail (~64px); preference persisted in localStorage
//   - on narrow widths, opens the slide-in drawer
// Sidebar is nav-only — Settings + Sign out pinned at the bottom; no logo
// or toggle inside the sidebar (those live in the header).

// #web-remove-messages-menu — Messages removed from the left nav.
// Conversations are accessed via Alerts → Messages tab (the
// MessagesTab in Notifications.tsx renders the same useChatList
// data with the same per-row Link to /chat/:userId). /chat/:userId
// conversation detail route stays; the /chat list route is gone.
const NAV_ITEMS: Array<{ to: string; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { to: '/',              label: 'Home',      icon: House },
  { to: '/relatives',     label: 'Relatives', icon: Users },
  { to: '/post',          label: 'Post',      icon: Plus },
  { to: '/tree/me',       label: 'Tree',      icon: GitBranch },
  { to: '/notifications', label: 'Alerts',    icon: Bell },
  { to: '/circles',       label: 'Circles',   icon: Users },
]

const COLLAPSED_KEY = 'sidebarCollapsed'

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1' } catch { return false }
}

// One source of truth for "are we on a desktop-width viewport". md = 768px.
function isDesktopViewport(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true
  return window.matchMedia('(min-width: 768px)').matches
}

export function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const queryClient = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed())
  // #web-alerts-badge: web-equivalent of mobile useAlertsCount. Sums
  // pending connection requests + unread chat messages + actionable
  // merge proposals at 30s cadence. Badge renders only when > 0.
  const alertsCount = useAlertsCount(user?.id)

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0') } catch { /* ignore */ }
  }, [collapsed])

  // Tree route needs the viewer's own id; substitute it in once authenticated.
  const navItems = NAV_ITEMS.map((it) =>
    it.to === '/tree/me' && user?.id ? { ...it, to: `/tree/${user.id}` } : it,
  )

  // Close the drawer on every route change so navigating from inside it
  // returns the user straight to the new page.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userId')
    localStorage.removeItem('user')
    clearAuth()
    // Drop the React Query cache — without this the next-logged-in
    // user briefly sees the prior account's feed / relatives / etc.
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : ''

  // Hamburger: collapse the rail on desktop, open the drawer on mobile.
  const handleMenuClick = () => {
    if (isDesktopViewport()) setCollapsed((v) => !v)
    else setDrawerOpen(true)
  }

  // Shared renderers — `iconOnly` collapses the row into a centred icon
  // for the desktop rail. The drawer always renders in expanded mode.
  const renderNavList = (iconOnly: boolean) => (
    <nav className="flex-1 flex flex-col gap-1 p-3" aria-label="Primary">
      {navItems.map(({ to, label, icon: Icon }) => {
        // #web-alerts-badge: Alerts gets a count badge; other items
        // render unchanged. Capped at 99+ so the chip doesn't blow
        // out the icon-rail width.
        const showBadge = label === 'Alerts' && alertsCount > 0
        const badgeText = alertsCount > 99 ? '99+' : String(alertsCount)
        return (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            title={iconOnly ? label : undefined}
            aria-label={showBadge ? `${label} (${alertsCount} new)` : label}
            className={({ isActive }) =>
              `relative flex items-center ${iconOnly ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-semibold transition outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${
                isActive
                  ? 'bg-white/10 text-gold'
                  : 'text-white/80 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            <span className="relative inline-flex">
              <Icon size={18} />
              {showBadge && (
                <span
                  // Position the badge over the icon's upper-right
                  // regardless of expanded/collapsed mode. min-w
                  // keeps single-digit and "99+" both readable.
                  className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-gold text-navy text-[10px] font-bold leading-[16px] text-center"
                  aria-hidden="true"
                >
                  {badgeText}
                </span>
              )}
            </span>
            {!iconOnly && <span>{label}</span>}
          </NavLink>
        )
      })}
    </nav>
  )

  const renderFooter = (iconOnly: boolean) => (
    <div className="p-3 border-t border-white/10 flex flex-col gap-2">
      <NavLink
        to="/settings"
        title={iconOnly ? 'Settings' : undefined}
        aria-label="Settings"
        className={({ isActive }) =>
          `flex items-center ${iconOnly ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-semibold transition outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${
            isActive ? 'bg-white/10 text-gold' : 'text-white/80 hover:bg-white/5 hover:text-white'
          }`
        }
      >
        <Settings size={18} />
        {!iconOnly && <span>Settings</span>}
      </NavLink>
      <button
        onClick={handleLogout}
        title={iconOnly ? 'Sign out' : undefined}
        aria-label="Sign out"
        className={`flex items-center ${iconOnly ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-md text-sm font-semibold text-danger hover:bg-white/5 transition outline-none focus-visible:ring-2 focus-visible:ring-danger/60`}
      >
        <LogOut size={18} />
        {!iconOnly && <span>Sign out</span>}
      </button>
    </div>
  )

  return (
    <div className="flex flex-col h-dvh bg-chatBg text-navy">
      {/* Top bar — spans the full viewport width above the sidebar. Bar
          height intentionally generous so the wordmark reads as a primary
          brand element rather than chrome. */}
      <header className="flex items-center justify-between px-3 md:px-5 py-3 bg-navy text-white shadow shrink-0">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <button
            onClick={handleMenuClick}
            className="text-gold p-1.5 rounded-md hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            aria-label={isDesktopViewport() ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : 'Open menu'}
            aria-expanded={!collapsed}
            aria-controls="primary-sidebar"
          >
            <Menu size={26} />
          </button>
          <img
            src="/logo-wide.png"
            alt="jnati"
            className="h-12 md:h-14 w-auto select-none"
          />
          {/* Admin entry point — visible only for users with is_admin=true.
              Same amber styling as the badge inside the admin surface so
              the two read as the same affordance. Click → /admin. */}
          {user?.isAdmin && (
            <Link
              to="/admin"
              className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-900 text-[11px] font-extrabold uppercase tracking-wider transition outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              title="Open admin console"
            >
              Admin
            </Link>
          )}
        </div>
        <button
          onClick={() => user?.id && navigate(`/profile/${user.id}`)}
          className="flex items-center gap-3 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
          title="Your profile"
        >
          <span className="hidden sm:inline text-sm font-semibold">
            {user ? `${user.firstName} ${user.lastName}` : ''}
          </span>
          {/* #bug-pair-1.7: header avatar — used to be initials-only,
              ignoring user.profilePhoto entirely. Now renders the
              photo when present (via resolveMediaUrl to turn the
              backend's relative path into an absolute URL) and falls
              back to gold-on-navy initials. Inline rather than via
              <Avatar> so we keep the inverted theme (gold bg / navy
              text) that the header has always used; <Avatar>'s
              default is navy/white. */}
          {(() => {
            const photo = resolveMediaUrl(user?.profilePhoto ?? null)
            return photo ? (
              <img
                src={photo}
                alt={user ? `${user.firstName} ${user.lastName}` : 'You'}
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gold text-navy font-bold flex items-center justify-center">
                {initials}
              </div>
            )
          })()}
        </button>
      </header>

      {/* Body: sidebar + scrolling main, side by side under the top bar. */}
      <div className="flex flex-1 min-h-0">
        {/* Desktop side rail — nav-only, full height of the body region. */}
        <aside
          id="primary-sidebar"
          className={`hidden md:flex flex-col bg-navy text-white shrink-0 transition-[width] duration-200 ease-out ${
            collapsed ? 'w-16' : 'w-60'
          }`}
        >
          {renderNavList(collapsed)}
          {renderFooter(collapsed)}
        </aside>

        {/* Mobile drawer — slide-in from the left, always expanded. */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Navigation">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <aside className="relative w-64 max-w-[80%] bg-navy text-white flex flex-col shadow-xl">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-end">
                <button
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close menu"
                  className="text-gold p-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <X size={22} />
                </button>
              </div>
              {renderNavList(false)}
              {renderFooter(false)}
            </aside>
          </div>
        )}

        {/* Main column — the only thing that scrolls. Erase-pending
            banner rides above it so it's visible on every page during
            the 30-day grace window.

            #font-split-nav-content: `font-content` (= Inter, per
            tailwind.config.ts) overrides the inherited Nunito for
            every post-login surface rendered inside <Outlet />.
            AppShell's header + sidebar + drawer above stay on
            Nunito by inheritance. */}
        <main className="flex-1 min-w-0 overflow-y-auto font-content">
          <ErasePendingBanner />
          <Outlet />
        </main>
      </div>
      {/* #gender-required-alerts (Part 1C) — global one-shot-per-session
          modal for the ~10 legacy accounts with gender=NULL. Mounts
          once at the AppShell root so every authenticated route sees
          it; component self-gates on user.gender === null. */}
      <GenderPromptModal />
    </div>
  )
}
